/**
 * Water mobs, round 3 (vanilla Java numbers):
 *  - axolotl: 14 HP, five colours (blue 1 in 1200), hunts fish / squid /
 *    drowned / guardians for 2, plays dead when hurt in water (regenerates),
 *    dries out after 5 minutes on land; bred with tropical fish.
 *  - dolphin: 10 HP, fast, neutral (a hit angers the pod, 3 damage), swims
 *    along with a swimming player, suffocates on land. 0-1 cod.
 *  - guardian: 30 HP, laser: 80 ticks of charge on a target in sight within
 *    15 blocks, then 6 damage (hard: 9); spikes hurt a melee attacker for 2.
 *    Prismarine shards, crystals or fish. 10 XP.
 *  - elder guardian: 80 HP, 8 laser damage, never despawns, gives Mining
 *    Fatigue III for 5 minutes to a player within 50 blocks once a minute;
 *    drops a sponge.
 */
import { BLOCKS, IS_WATERY } from '../../blocks';
import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Animal } from '../animals';
import { Mob, type MobCtx } from '../mob';
import { overworld2Hooks } from '../overworld2/mobs';
import { mobs3Hooks, nearestMob, ri } from './hooks';

const watery = (m: Mob & { w: { getBlock(x: number, y: number, z: number): number } }, x: number, y: number, z: number): boolean =>
  IS_WATERY[m.w.getBlock(Math.floor(x), Math.floor(y), Math.floor(z))] === 1;

/** A swim target and the steering toward it, shared by everything in this file. */
class Swim {
  tx = 0; ty = 0; tz = 0; retarget = 0;
  pick(m: Mob, w: { getBlock(x: number, y: number, z: number): number }, r: () => number, far: number): void {
    this.tx = m.x; this.ty = m.y; this.tz = m.z; this.retarget = 20;
    for (let i = 0; i < 10; i++) {
      const x = m.x + (r() * 2 - 1) * far, y = m.y + (r() * 2 - 1) * far * 0.4, z = m.z + (r() * 2 - 1) * far;
      if (IS_WATERY[w.getBlock(Math.floor(x), Math.floor(y), Math.floor(z))] === 1 && IS_WATERY[w.getBlock(Math.floor(x), Math.floor(y + 0.6), Math.floor(z))] === 1) {
        this.tx = x; this.ty = y; this.tz = z; this.retarget = 40 + Math.floor(r() * 60); return;
      }
    }
  }
  /** Accelerate `m` toward (x, y, z) at `sp` blocks / tick; returns the horizontal heading. */
  go(m: Mob, x: number, y: number, z: number, sp: number): void {
    const dx = x - m.x, dy = y - m.y, dz = z - m.z;
    const d = Math.hypot(dx, dy, dz) || 1;
    m.vx += (dx / d * sp - m.vx) * 0.15;
    m.vz += (dz / d * sp - m.vz) * 0.15;
    m.vy += (dy / d * sp - m.vy) * 0.15 + 0.02; // cancels water gravity
    m.pitch = -Math.atan2(dy, Math.hypot(dx, dz) || 1) * 0.5;
  }
  wander(m: Mob, w: { getBlock(x: number, y: number, z: number): number }, r: () => number, sp: number, far = 6): void {
    if (--this.retarget <= 0 || (this.tx - m.x) ** 2 + (this.ty - m.y) ** 2 + (this.tz - m.z) ** 2 < 1) this.pick(m, w, r, far);
    this.go(m, this.tx, this.ty, this.tz, sp);
  }
}

/** Beached: flop about and, past `limit` ticks, suffocate. */
function flop(m: Mob, r: () => number, air: number, limit: number): void {
  if (m.onGround && r() < 0.12) { m.vy = 0.25; m.vx = (r() - 0.5) * 0.2; m.vz = (r() - 0.5) * 0.2; }
  if (air > limit && air % 20 === 0) m.hurt(1);
}

// ----------------------------------------------------------------- axolotl

export const AXOLOTL_COLORS = ['lucy', 'wild', 'gold', 'cyan', 'blue'] as const;
const AXOLOTL_PREY = new Set<MobType>(['cod', 'salmon', 'squid', 'drowned', 'guardian', 'elder_guardian']);
export const AXOLOTL_DRY_TICKS = 6000;

export class Axolotl extends Animal {
  protected raw = 0; protected cooked = 0;
  variant: number;
  playDead = 0;
  private hunting = false;
  private huntCd = 0;
  private attackCd = 0;
  private air = 0;
  private readonly swim = new Swim();
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'axolotl' as 'pig', x, y, z, { width: 0.75, height: 0.42, eye: 0.28, health: 14, speed: 0.1 }, rnd);
    this.variant = rnd() < 1 / 1200 ? 4 : Math.floor(rnd() * 4);
    this.noFallDamage = true;
  }
  protected override floats(): boolean { return false; }
  override onHurtBy(fromX: number, fromZ: number, rnd: () => number): void {
    // vanilla: in water, a third of the hits (always below half health) make it play dead for 10 s
    if (this.inWater && this.playDead === 0 && (rnd() < 1 / 3 || this.health < this.maxHealth / 2)) this.playDead = 200;
    else super.onHurtBy(fromX, fromZ, rnd);
  }
  override inherit(a: Animal, b: Animal, rnd: () => number): void {
    this.variant = rnd() < 1 / 1200 ? 4 : ((rnd() < 0.5 ? a : b) as Axolotl).variant ?? this.variant;
  }
  protected override saveMore(o: Record<string, number>): void { o.v = this.variant; if (this.playDead > 0) o.d = 1; }
  protected override loadMore(o: Record<string, number>): void { this.variant = (o.v | 0) % AXOLOTL_COLORS.length; this.color = this.variant | (o.d ? 8 : 0); }

  protected override ai(ctx: MobCtx): void {
    this.color = this.variant | (this.playDead > 0 ? 8 : 0);
    const r = ctx.host.rnd;
    if (this.attackCd > 0) this.attackCd--;
    if (this.huntCd > 0) this.huntCd--;
    if (!this.inWater) {
      // crawls on land, dries out after five minutes (rain keeps it wet)
      if (!ctx.host.isRainingAt?.(Math.floor(this.x), Math.floor(this.y + 1), Math.floor(this.z))) this.air++;
      if (this.air > AXOLOTL_DRY_TICKS && this.air % 20 === 0) this.hurt(2);
      super.ai(ctx);
      return;
    }
    this.air = 0;
    this.ageTick(ctx);
    if (this.playDead > 0) {
      this.playDead--;
      if (this.playDead % 50 === 0 && this.health < this.maxHealth) this.health++; // Regeneration I
      this.vx *= 0.8; this.vz *= 0.8; this.vy += 0.018;
      return;
    }
    const goal = this.mate?.alive ? this.mate : this.follow?.alive ? this.follow : null;
    if (goal) { this.swim.go(this, goal.x, goal.y, goal.z, 0.08); this.faceToward(goal.x, goal.z); return; }
    const prey = !this.baby && this.huntCd === 0 && (this.phase(ctx, 10) || this.hunting)
      ? nearestMob(this, 8, (m) => AXOLOTL_PREY.has(m.type) && m.inWater && m.alive) : null;
    this.hunting = prey !== null;
    if (prey) {
      this.aggressive = true;
      this.swim.go(this, prey.x, prey.y + prey.height / 2, prey.z, 0.1);
      this.faceToward(prey.x, prey.z);
      if (this.attackCd === 0 && (prey.x - this.x) ** 2 + (prey.y - this.y) ** 2 + (prey.z - this.z) ** 2 < (1 + prey.width / 2) ** 2) {
        this.attackCd = 20;
        mobs3Hooks.hurtMob(prey, 2, this, 0.2);
        // vanilla: two minutes off after killing a passive fish
        if (!prey.alive && !mobs3Hooks.isHostile(prey.type)) this.huntCd = 2400;
      }
      return;
    }
    this.swim.wander(this, this.w, r, this.panicTicks > 0 ? 0.12 : 0.05);
    if (this.panicTicks > 0) this.panicTicks--;
    this.faceToward(this.swim.tx, this.swim.tz);
  }
  override loot(): Stack[] { return []; }
}

// ----------------------------------------------------------------- dolphin

export const DOLPHIN_AIR_TICKS = 2400;

export class Dolphin extends Mob {
  anger = 0;
  private air = 0;
  private attackCd = 0;
  private readonly swim = new Swim();
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'dolphin', x, y, z, { width: 0.9, height: 0.6, eye: 0.3, health: 10, speed: 0.3 }, rnd);
    this.noFallDamage = true;
  }
  protected override floats(): boolean { return false; }
  override onHurtBy(): void {
    for (const m of mobs3Hooks.mobs()) if (m instanceof Dolphin && (m.x - this.x) ** 2 + (m.z - this.z) ** 2 < 16 * 16) m.anger = 400;
  }
  protected ai(ctx: MobCtx): void {
    const r = ctx.host.rnd, p = ctx.host.player;
    if (this.attackCd > 0) this.attackCd--;
    if (this.anger > 0) this.anger--;
    if (!this.inWater) { flop(this, r, ++this.air, DOLPHIN_AIR_TICKS); return; }
    this.air = 0;
    const d2 = (p.x - this.x) ** 2 + (p.y - this.y) ** 2 + (p.z - this.z) ** 2;
    const pw = watery(this as never, p.x, p.y + 0.4, p.z);
    if (this.anger > 0 && ctx.targetable() && d2 < 20 * 20 && pw) {
      this.aggressive = true;
      this.swim.go(this, p.x, p.y + 0.6, p.z, 0.16);
      this.faceToward(p.x, p.z);
      if (this.attackCd === 0 && d2 < 1.6 * 1.6) { this.attackCd = 20; ctx.hurtPlayer(this, 3, 0.3); }
      return;
    }
    // DolphinSwimWithPlayerGoal: keeps a swimmer company
    if (pw && d2 < 10 * 10 && d2 > 3 * 3 && p.alive) {
      this.swim.go(this, p.x, p.y + 0.6, p.z, 0.14);
      this.faceToward(p.x, p.z);
      return;
    }
    this.swim.wander(this, this.w, r, 0.12, 10);
    // turn back before leaving the water
    if (!watery(this as never, this.x + this.vx * 6, this.y, this.z + this.vz * 6)) this.swim.pick(this, this.w, r, 4);
    this.faceToward(this.swim.tx, this.swim.tz);
  }
  xpValue(rnd: () => number): number { return ri(rnd ?? Math.random, 1, 3); }
  loot(rnd: () => number): Stack[] {
    return rnd() < 0.5 ? [stackOf(this.fireTicks > 0 ? I.COOKED_COD : I.COD, 1)] : [];
  }
}

// ---------------------------------------------------------------- guardian

export const GUARDIAN_CHARGE = 80;
export const ELDER_CURSE_PERIOD = 1200;
const GUARDIAN_PREY = new Set<MobType>(['squid', 'axolotl']);
let spongeId = -2;

export class Guardian extends Mob {
  /** The pose rides in the replicated record so clients draw it (server mode). */
  saveExtra(): unknown { return [this.color]; }
  loadExtra(d: unknown): void { if (Array.isArray(d)) this.color = Number(d[0]) | 0; }
  readonly elder: boolean;
  /** Laser charge in ticks (0 = no beam). */
  charge = 0;
  private prey: Mob | null = null;
  private curse = 0;
  private readonly swim = new Swim();
  constructor(id: number, x: number, y: number, z: number, rnd: () => number, elder = false) {
    super(id, elder ? 'elder_guardian' : 'guardian', x, y, z,
      elder ? { width: 1.9975, height: 1.9975, eye: 1, health: 80, speed: 0.3 } : { width: 0.85, height: 0.85, eye: 0.425, health: 30, speed: 0.5 }, rnd);
    this.elder = elder;
    this.noFallDamage = true;
    if (elder) { this.persistent = true; this.knockbackResist = 0.6; this.curse = ri(rnd, 100, 400); }
  }
  protected override floats(): boolean { return false; }

  /** Spikes: a melee hit while the guardian is not swimming away costs the attacker 2. */
  override onHurtBy(fromX: number, fromZ: number): void {
    const ctx = this.lastCtx;
    if (!ctx || !ctx.targetable()) return;
    const p = ctx.host.player;
    if (Math.abs(p.x - fromX) < 0.5 && Math.abs(p.z - fromZ) < 0.5 && (p.x - this.x) ** 2 + (p.z - this.z) ** 2 < 5 * 5) ctx.hurtPlayer(this, 2, 0);
  }
  private lastCtx: MobCtx | null = null;

  protected ai(ctx: MobCtx): void {
    this.lastCtx = ctx;
    const r = ctx.host.rnd, p = ctx.host.player;
    this.color = 0;
    if (!this.inWater) {
      this.charge = 0;
      if (this.onGround && r() < 0.15) { this.vy = 0.3; this.vx = (r() - 0.5) * 0.3; this.vz = (r() - 0.5) * 0.3; }
      return;
    }
    const d2 = (p.x - this.x) ** 2 + (p.eyeY - this.eyeY) ** 2 + (p.z - this.z) ** 2;
    // the elder's curse
    if (this.elder && --this.curse <= 0) {
      this.curse = ELDER_CURSE_PERIOD;
      if (ctx.targetable() && d2 < 50 * 50) {
        overworld2Hooks.hitEffect?.('mining_fatigue', 6000, 2);
        ctx.host.effect('poof', p.x, p.eyeY, p.z);
      }
    }
    // target: a player in sight, else a squid / axolotl
    const range = 16;
    let tx = 0, ty = 0, tz = 0, has = false;
    if (ctx.targetable() && d2 < range * range && ctx.los(this.x, this.eyeY, this.z, p.x, p.eyeY, p.z)) {
      tx = p.x; ty = p.y + p.height * 0.5; tz = p.z; has = true; this.prey = null;
    } else {
      if (this.phase(ctx, 20)) this.prey = this.elder ? null : nearestMob(this, 12, (m) => GUARDIAN_PREY.has(m.type) && ctx.los(this.x, this.eyeY, this.z, m.x, m.y + m.height / 2, m.z));
      const m = this.prey;
      if (m && m.alive && (m.x - this.x) ** 2 + (m.y - this.y) ** 2 + (m.z - this.z) ** 2 < range * range) { tx = m.x; ty = m.y + m.height / 2; tz = m.z; has = true; } else this.prey = null;
    }
    if (!has) {
      this.charge = 0;
      this.swim.wander(this, this.w, r, this.elder ? 0.04 : 0.07, 8);
      this.faceToward(this.swim.tx, this.swim.tz);
      return;
    }
    // GuardianAttackGoal: hold still, look at the target, charge the beam
    this.aggressive = true;
    this.faceToward(tx, tz);
    this.lookAt(tx, ty, tz);
    const dist = Math.hypot(tx - this.x, ty - this.eyeY, tz - this.z);
    if (dist > 9) this.swim.go(this, tx, ty, tz, 0.06);
    else { this.vx *= 0.8; this.vz *= 0.8; this.vy = this.vy * 0.8 + 0.02; }
    this.charge++;
    // the beam's length rides in `color` so the renderer can draw it (quarter blocks)
    this.color = Math.max(1, Math.min(255, Math.round(dist * 4)));
    this.swell = this.charge / GUARDIAN_CHARGE;
    if (this.charge >= GUARDIAN_CHARGE) {
      this.charge = 0;
      const hard = ctx.host.difficulty?.() === 'hard';
      const dmg = (this.elder ? 8 : 6) + (hard ? (this.elder ? 4 : 3) : 0);
      if (this.prey) mobs3Hooks.hurtMob(this.prey, dmg, this, 0);
      else ctx.hurtPlayer(this, dmg, 0);
    }
  }

  xpValue(): number { return 10; }
  loot(rnd: () => number, byPlayer = false): Stack[] {
    const out: Stack[] = [];
    const n = ri(rnd, 0, 2);
    if (n) out.push(stackOf(I.PRISMARINE_SHARD, n));
    const roll = rnd();
    const fish = this.fireTicks > 0 ? I.COOKED_COD : I.COD;
    if (this.elder) {
      if (roll < 0.5) out.push(stackOf(fish, 1)); else if (roll < 0.83) out.push(stackOf(I.PRISMARINE_CRYSTALS, 1));
      if (byPlayer) {
        if (spongeId === -2) spongeId = BLOCKS.findIndex((b) => b?.name === 'Sponge');
        if (spongeId > 0) out.push(stackOf(spongeId, 1));
      }
    } else if (roll < 0.4) out.push(stackOf(fish, 1));
    else if (roll < 0.8) out.push(stackOf(I.PRISMARINE_CRYSTALS, 1));
    if (byPlayer && rnd() < 0.025) out.push(stackOf(rnd() < 0.6 ? I.COD : rnd() < 0.6 ? I.SALMON : I.PUFFERFISH, 1));
    return out;
  }
}
